// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { Contract, Entity, ErrorCause, Feedback } from '../../../@types';

// router
import { useParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { getOptionsActStatuses } from '../../../utils/options';
import { translateError } from '../../../utils/translations';

// props
export interface FormAddActProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddAct = ({ onClose, onSuccess, setIsLoading }: FormAddActProps) => {
	// hooks
	const { idSupplier } = useParams();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [values, setValues] = useState({
		contracts: [],
		dateStart: '',
		dateEnd: '',
		description: '',
		entities: idSupplier ? [idSupplier] : [],
		name: '',
		status: 'STARTED',
	});

	// vars
	const idForm = 'form-add-activity';

	// fetch suppliers
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=SUPPLIER&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (data) => {
			if (data.data) {
				setEntities(data.data);
			}
		},
	});

	// fetch suppliers contracts
	const { fetchRequest: fetchContracts } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/contracts?project=${
			project?.id
		}&entities=${values.entities.join(',')}&fields=amounts,id,num`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (data) => {
			if (data.data) {
				setContracts(data.data);
			}
		},
	});

	// options
	const optionsContracts = contracts.map((contract) => ({
		label: contract.num,
		secondary:
			contract.amounts.length > 0
				? contract.amounts.map((amount) => amount.currency).join(', ')
				: undefined,
		value: String(contract.id),
	}));

	const optionsEntities = entities.map((entity) => ({
		disabled: String(entity.id) === idSupplier,
		label: entity.name,
		value: String(entity.id),
	}));

	const optionsStatus = getOptionsActStatuses({ hasDescription: true, t });

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;

		let contractsNew = values.contracts;
		if (name === 'entities' && !value.length) {
			contractsNew = [];
		}

		setValues({
			...values,
			contracts: contractsNew,
			[name]: value,
		});
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// set loader
			setIsLoading(true);

			const fetchAddPackage = await fetch(`${process.env.REACT_APP_API_URL}/acts`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					contracts: values.contracts.map((contract) => Number(contract)),
					dateEnd: values.dateEnd,
					dateStart: values.dateStart,
					description: values.description,
					entities: values.entities.map((entity) => Number(entity)),
					name: values.name,
					project: project?.id,
					status: values.status,
				}),
			});
			const resAddPackage = await fetchAddPackage.json();

			if (resAddPackage.error) {
				throw new Error(resAddPackage.error.message, {
					cause: {
						id: resAddPackage.error.id,
					},
				});
			}

			const act = resAddPackage.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append('path', `bldt/projects/${project?.id}/acts/${act.id}/${year}/${month}/`);
				formData.append('file', files[i]);
				formData.append('act', String(act.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({ alert: { severity: 'error' }, response: resPostDoc });
				}
			}

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				if (onSuccess) {
					onSuccess();
				}

				// set success banner
				setBanner({
					hasClose: true,
					message: t('alert.actCreated', { name: values.name }),
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	let dateStartMax = null;
	if (values.dateEnd) {
		dateStartMax = new Date(values.dateEnd);
	}

	let dateEndMin = new Date();
	if (values.dateStart) {
		dateEndMin = new Date(values.dateStart);
	}

	// fetch contracts when entities change
	useEffect(() => {
		fetchContracts();
	}, [fetchContracts, values.entities]);

	return (
		<form id={idForm} name="formAddAct" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-status`}
						label={t('status')}
						name="status"
						onChange={handleChangeValue}
						options={optionsStatus}
						required={true}
						value={values.status}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						id={`${idForm}-entities`}
						label={t('suppliers')}
						multiple={true}
						name="entities"
						onChange={handleChangeValue}
						options={optionsEntities}
						required={true}
						value={values.entities}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Select
						disabled={!values.entities.length}
						helperText={
							values.entities.length ? undefined : t('formAddTransaction.payment.contract.helper')
						}
						id={`${idForm}-contracts`}
						label={t('contracts')}
						multiple={true}
						name="contracts"
						onChange={handleChangeValue}
						options={optionsContracts}
						required={true}
						value={values.contracts}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-start`}
						inputProps={{
							// start date must be before end date
							max: dateStartMax ? dateStartMax.toISOString().split('T')[0] : '',
						}}
						label={t('startDate')}
						name="dateStart"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateStart}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-end`}
						inputProps={{
							// end date must be after start date
							min: dateEndMin.toISOString().split('T')[0],
						}}
						label={t('endDate')}
						name="dateEnd"
						onChange={handleChangeValue}
						required={true}
						type="date"
						value={values.dateEnd}
					/>
				</Grid>
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setFiles(filesSelected);
						}}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-description`}
						label={t('description')}
						minRows={4}
						multiline={true}
						name="description"
						onChange={handleChangeValue}
						value={values.description}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
